import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ErrorMessage - User-friendly error message component
 * 
 * @param {Object} props
 * @param {string} props.title - Error title (default: "Something went wrong")
 * @param {string} props.message - Human-readable error message
 * @param {string} props.technicalError - Technical error details (optional, for debugging)
 * @param {string} props.actionLabel - Label for retry/action button
 * @param {Function} props.onAction - Action button click handler
 * @param {string} props.severity - 'error' | 'warning' | 'info'
 * @param {boolean} props.showTechnical - Show technical error details (default: false)
 * @param {Object} props.sx - Additional styling
 */
const ErrorMessage = ({
  title = 'Something went wrong',
  message,
  technicalError,
  actionLabel = 'Try Again',
  onAction,
  severity = 'error',
  showTechnical = false,
  sx = {},
  ...props
}) => {
  // Convert technical errors to user-friendly messages
  const getUserFriendlyMessage = (error) => {
    if (!error) return message || 'An unexpected error occurred. Please try again.';
    
    const errorStr = typeof error === 'string' ? error : error.message || String(error);
    
    // Network errors
    if (errorStr.includes('Network Error') || errorStr.includes('Failed to fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    // 404 errors
    if (errorStr.includes('404') || errorStr.includes('Not Found')) {
      return 'The requested resource could not be found. It may have been moved or deleted.';
    }
    
    // 403 errors
    if (errorStr.includes('403') || errorStr.includes('Forbidden')) {
      return 'You don\'t have permission to access this resource.';
    }
    
    // 401 errors
    if (errorStr.includes('401') || errorStr.includes('Unauthorized')) {
      return 'Your session has expired. Please sign in again.';
    }
    
    // 500 errors
    if (errorStr.includes('500') || errorStr.includes('Internal Server Error')) {
      return 'The server encountered an error. Our team has been notified. Please try again in a few moments.';
    }
    
    // Timeout errors
    if (errorStr.includes('timeout') || errorStr.includes('Timeout')) {
      return 'The request took too long to complete. Please try again.';
    }
    
    // Validation errors
    if (errorStr.includes('validation') || errorStr.includes('Validation')) {
      return 'Please check your input and try again.';
    }
    
    // Default: use provided message or return generic
    return message || 'An unexpected error occurred. Please try again.';
  };

  const userMessage = getUserFriendlyMessage(technicalError || message);

  return (
    <Box
      sx={{
        width: '100%',
        p: { xs: 2, md: 3 },
        ...sx,
      }}
      {...props}
    >
      <Alert
        severity={severity}
        icon={<ErrorOutlineIcon />}
        sx={{
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </AlertTitle>
        
        <Typography variant="body1" sx={{ mb: showTechnical && technicalError ? 2 : 0 }}>
          {userMessage}
        </Typography>
        
        {showTechnical && technicalError && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: 'text.secondary',
              overflow: 'auto',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Technical Details:
            </Typography>
            <Typography variant="caption" component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {typeof technicalError === 'string' 
                ? technicalError 
                : JSON.stringify(technicalError, null, 2)}
            </Typography>
          </Box>
        )}
        
        {onAction && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color={severity}
              startIcon={<RefreshIcon />}
              onClick={onAction}
              sx={{
                mt: 1,
                minHeight: 44,
              }}
            >
              {actionLabel}
            </Button>
          </Box>
        )}
      </Alert>
    </Box>
  );
};

export default ErrorMessage;

